import { useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import Button from '@/components/ui/button'
// import { CrossIcon } from 'common/components/icons'
import { i18n } from '@/locale/i18n'
import './PlaceBet.scss'
import { useActiveCurrency, useCurrencies, useCurrentUser } from '@/hooks'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { CoinsIcon, Crevron, CrossIcon, OomsIcon } from '@/assets/icons'
import { depositUrl, keepQueryString, questionDetailUrl } from '@/urls'
// import EmailConfirmationRequired from 'app/components/email-confirmation-required'
import constants from '@/constants'
import { WAGER_STATUS } from '@/constants/marketBackendEnums'
import { useDispatch, useSelector } from 'react-redux'
import { selectUserFromAllowedCountry } from '@/store/account'
import { getWagerSelected, selectLastBetStatus, selectWagerSelected } from '@/store/wagers'
import { shortenText } from '@/util/textFormat'
import { selectSelectedLimitPrice, setActiveCurrency, setSelectedLimitPrice } from '@/store/app'
import storage from '@/util/storage'
import { toastService } from '@/api/toast'
import { Sheet } from 'react-modal-sheet'
import { getSheetExitMs } from '@/layouts/modal/sheetTiming'
import BuyPlaceBet from '../buy-place-bet'
import SellPlaceBet from '../sell-place-bet'
import IconContainer from '@/components/ui/iconContainer'
import useIsMobileSize from '@/hooks/useIsMobileSize'
import CheckControl from '@/components/ui/CheckControl'
import useOrderBookRealTime from '@/hooks/useOrderBookRealTime'
import ArrowRightTailed from '@/assets/icons/arrowRightTailed'
import OrderTypeToggle from './OrderTypeToggle'
import PlaceBetBlockedContent from './PlaceBetBlockedContent'
import { getPlaceBetBlockReason, PLACE_BET_BLOCK_REASON } from './placeBetBlockReason'
import mixpanelService from '@/services/analytics/Mixpanel'
import { CLICK_COMPONENT, CLICK_ELEMENT, CLICK_SOURCE } from '@/constants/analyticsClick'
import { useSiteSnapshot } from '@/features/site-control/useSiteControl'

/**
 * ? visit renderPlaceBetMainContent for the main place bet component
 */

const PlaceBet = ({
  question,
  onToggle,
  /**
   * Render the sheet layout on a screen that is not phone-sized. The soccer list
   * docks its pad in a sheet below the widths that fit a column, and wants the
   * sheet's own header, close button, backdrop and drag-to-dismiss.
   */
  asSheet = false,
  /**
   * Pinned in a surface of its own — the list's bet column. It names the market it is
   * aimed at, the way the sheet's header does, and offers no way out: the column is
   * always there, and closing the pad would only leave it empty.
   */
  docked = false,
  quickbet = false,
  open = false,
  outcome,
  onChangeOutcome,
  onCancel = () => {},
  place,
  onWagerAction,
  position,
  setPosition,
  setPlacebetOpen = () => {},
  onOpenOrderbook = () => {},
  onScrollToOrderbook = () => {},
  onSelectOrderbookPrice = () => {},
  inMarketPage = false,
  onSetActiveTabRef = null,
}) => {
  const currencyId = useActiveCurrency().id
  const fromAllowedCountry = useSelector(selectUserFromAllowedCountry())
  const { tradingPaused, message: pauseMessage } = useSiteSnapshot()
  const lastBetStatus = useSelector(selectLastBetStatus())
  const [activeTab, setActiveTab] = useState('buy')
  const [orderType, setOrderType] = useState('market') // 'market' or 'limit'

  // Expose setActiveTab to parent via ref callback
  useEffect(() => {
    if (onSetActiveTabRef) {
      onSetActiveTabRef.current = setActiveTab
    }
  }, [onSetActiveTabRef])
  const buyPlaceBetRef = useRef(null)
  const sellPlaceBetRef = useRef(null)
  const wagerSelected = useSelector(selectWagerSelected())
  const isMobileSize = useIsMobileSize()
  /* Only the presentation switches; the phone-specific behaviour below still keys
     off `isMobileSize`. */
  const renderAsSheet = asSheet || isMobileSize
  const location = useLocation()
  const user = useCurrentUser()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const currencies = useCurrencies().currencies

  const selectedLimitPrice = useSelector(selectSelectedLimitPrice)
  const defaultActionRef = useRef(null) // Track if we have a default action from URL

  // Non-stable real money on sell: allow market sell only, disable switching to limit
  const sellWagerCurrency = wagerSelected && currencies[wagerSelected.currency]
  const sellLimitDisabled =
    activeTab === 'sell' &&
    wagerSelected &&
    sellWagerCurrency &&
    !sellWagerCurrency.is_stable &&
    wagerSelected.currency !== 'OOM'

  // Step 1: Check for default_action parameter on mount and set activeTab
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search)
    const defaultAction = urlParams.get('default_action')

    if (defaultAction === 'sell' || defaultAction === 'buy') {
      defaultActionRef.current = defaultAction // Store it in ref
      setActiveTab(defaultAction)
      console.log(`✅ Set activeTab to ${defaultAction} from URL parameter`)
    }
  }, []) // Empty dependency array = run once on mount

  // Subscribe to real-time order book updates
  useOrderBookRealTime(
    outcome?.id && position && question?.id
      ? [
          {
            marketId: question.id,
            currency: currencyId === 'OOM' ? 'OOM' : question.canonical_currency,
            outcome: outcome.id,
            positionType: position,
          },
        ]
      : []
  )
  const [betPlaced, setBetPlaced] = useState(false) // to display "bet again popup"
  const [showOrderConflict, setShowOrderConflict] = useState(false)
  const [orderConflictData, setOrderConflictData] = useState(null)
  const [dontAskAgain, setDontAskAgain] = useState(false)
  // Mobile renders the bet pad inside a Sheet driven by `open`, so picking an outcome
  // (buy) or a wager (sell) has to open it. Only a *new* selection opens the sheet:
  // closing it clears the outcome, and that must not count as a selection or the sheet
  // would immediately reopen while a wager is still selected. Quickbet owns its own state.
  const previousSelectionRef = useRef({ initialized: false, outcomeId: null, wagerKey: null })
  // The sheet animates from `y = windowHeight` to 0 only on the render that mounts it with
  // `isOpen`. Kept mounted while closed it never runs that animation and stays hidden below
  // the fold, so it is mounted on open and unmounted once the exit animation has played.
  const [sheetMounted, setSheetMounted] = useState(open)
  useEffect(() => {
    if (!isMobileSize || quickbet) return

    const outcomeId = outcome?.id ?? null
    const wagerKey = wagerSelected ? `${wagerSelected.id}:${wagerSelected._diff ?? ''}` : null
    const previous = previousSelectionRef.current
    previousSelectionRef.current = { initialized: true, outcomeId, wagerKey }

    // Don't pop the sheet open just because the page mounted with a selection.
    if (!previous.initialized) return

    const selectedOutcome = outcomeId != null && outcomeId !== previous.outcomeId
    const selectedWager = wagerKey != null && wagerKey !== previous.wagerKey
    if (selectedOutcome || selectedWager) setPlacebetOpen(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobileSize, quickbet, outcome?.id, wagerSelected?.id, wagerSelected?._diff])

  useEffect(() => {
    if (!renderAsSheet) return undefined
    if (open) {
      setSheetMounted(true)
      return undefined
    }
    const timeout = window.setTimeout(() => setSheetMounted(false), getSheetExitMs())
    return () => window.clearTimeout(timeout)
  }, [open, renderAsSheet])

  // A manual wager selection carries a `_diff` nonce and switches to the sell tab once.
  // Tracking the nonce that was already applied keeps the user free to go back to buy
  // while that same wager stays selected.
  const wagerSelectionKey = wagerSelected ? `${wagerSelected.id}:${wagerSelected._diff ?? ''}` : null
  const appliedWagerSelectionRef = useRef(null)
  const shouldAutoSwitchToSell =
    wagerSelected && wagerSelected._diff !== undefined && appliedWagerSelectionRef.current !== wagerSelectionKey

  const handleTabChange = type => {
    appliedWagerSelectionRef.current = wagerSelectionKey
    setActiveTab(type)
  }

  useEffect(() => {
    if (wagerSelected) {
      setBetPlaced(false)
      if (shouldAutoSwitchToSell && activeTab === 'buy') {
        appliedWagerSelectionRef.current = wagerSelectionKey
        setActiveTab('sell')
      }
      // Switch to market order type if currently on limit
    } else if (!wagerSelected && activeTab === 'sell' && orderType === 'limit') {
      setOrderType('market')
    }
  }, [wagerSelected, orderType, activeTab, shouldAutoSwitchToSell, wagerSelectionKey])

  useEffect(() => {
    if (wagerSelected) {
      // Only set order type to market if wager was manually selected
      // Check if this is a manual selection by looking for the _diff property
      // which is added when manually selecting from MarketCard/BetList
      if (wagerSelected._diff !== undefined) {
        setOrderType('market')
      }
    }
  }, [wagerSelected])

  useEffect(() => {
    if (selectedLimitPrice && orderType === 'market') {
      setOrderType('limit')
    }
  }, [selectedLimitPrice])

  useEffect(() => {
    if (selectedLimitPrice && orderType === 'market') {
      dispatch(setSelectedLimitPrice(null))
    }
  }, [orderType])

  // When on sell with non-stable currency, force market order (limit sell disabled)
  useEffect(() => {
    if (activeTab === 'sell' && orderType === 'limit' && sellLimitDisabled) {
      setOrderType('market')
    }
  }, [activeTab, orderType, sellLimitDisabled])

  useEffect(() => {
    if (!outcome && !isMobileSize && position === constants.position.LONG && activeTab === 'buy')
      onChangeOutcome(question.outcomes[0])
  }, [outcome, isMobileSize, position, activeTab])

  // Store previous tab to detect tab changes and prevent clearing during tab switch
  const previousTabRef = useRef(activeTab)
  const isTabSwitchingRef = useRef(false)

  // Sync outcome when wager changes (for sell tab)
  useEffect(() => {
    // Skip if we're in the middle of a tab switch
    if (isTabSwitchingRef.current) return

    // If wager is selected and we have the _diff property (manual selection from MarketCard/BetList),
    // automatically switch to sell tab
    if (shouldAutoSwitchToSell && activeTab === 'buy') {
      appliedWagerSelectionRef.current = wagerSelectionKey
      setActiveTab('sell')
      // Set the outcome from the wager
      if (wagerSelected.outcome && (!outcome || outcome.id !== wagerSelected.outcome.id)) {
        onChangeOutcome(wagerSelected.outcome)
      }
      return
    }

    // Only sync outcome when wager is selected and we're on sell tab
    if (wagerSelected && activeTab === 'sell') {
      // Set the outcome from the wager
      if (wagerSelected.outcome && (!outcome || outcome.id !== wagerSelected.outcome.id)) {
        onChangeOutcome(wagerSelected.outcome)
      }
    } else if (wagerSelected && activeTab === 'buy') {
      // On buy tab, clear wager selection if outcome doesn't match
      if (outcome && wagerSelected.outcome?.id !== outcome.id) {
        dispatch(getWagerSelected(null))
      }
    }
  }, [wagerSelected, activeTab, outcome, onChangeOutcome, dispatch, shouldAutoSwitchToSell, wagerSelectionKey])

  // Close order conflict panel when key parameters change
  useEffect(() => {
    if (showOrderConflict) {
      setShowOrderConflict(false)
      setOrderConflictData(null)
      setDontAskAgain(false) // Reset checkbox state
    }
  }, [activeTab, wagerSelected, outcome])

  // Auto-select the first outcome (Yes) for yes/no questions if no outcome is selected

  const renderPlaceBetTemplate = (content, contentStyle = {}, contentClass = '') => {
    const quickbetHeader = (
      <>
        <Link className="font-bold text-Neutral-6 text-sm" to={keepQueryString(questionDetailUrl(question))}>
          <h1>{shortenText(question.title, 40)}</h1>
        </Link>
        <IconContainer size={12} onClick={onToggle}>
          <CrossIcon className="cursor-pointer" width="12px" height="12px" fillcolor="currentColor" />
        </IconContainer>
      </>
    )
    /* The sheet carries this in `Sheet.Header`; docked there is no sheet to carry it,
       and without it the column gives no sign of which row it is aimed at. `px-5` lines
       the title up with the buy/sell chips below, which set their own. */
    const dockedHeader = docked && !renderAsSheet && (
      <div className="flex items-center px-5 pt-4 pb-4 text-Neutral-6">
        {/* A span, not the sheet's `h1`: the column lives on the feed, which has a
            heading of its own, and this is a label for the pad rather than one. */}
        <Link className="font-bold text-Neutral-6 text-sm" to={keepQueryString(questionDetailUrl(question))}>
          <span>{shortenText(question.title, 40)}</span>
        </Link>
      </div>
    )

    const fullComp = (
      <div
        className={classNames('place-bet', {
          '!rounded-t-none': quickbet && !docked,
          '!w-full': quickbet,
          'border border-solid border-Neutral-3': (!quickbet && !renderAsSheet) || (docked && !renderAsSheet),
        })}
        id="place-bet">
        {dockedHeader}
        <div
          className={classNames('', contentClass, {
            'place-bet__content--quickbet': quickbet,
          })}
          style={contentStyle}>
          {content}
        </div>
      </div>
    )
    if (renderAsSheet && !open && !sheetMounted) return null

    return renderAsSheet ? (
      <Sheet
        isOpen={open}
        /* The phone sheet claims pointer events for its whole overlay; the docked one
           must let them through to the list, and an `!important` utility here would
           outrank the stylesheet (important declarations invert layer order). */
        className={classNames({ '!pointer-events-auto': !asSheet, 'place-bet-sheet--docked': asSheet })}
        onClose={() => {
          onToggle(false)
          // Clear outcome selection when closing to allow re-selection
          if (onCancel) onCancel()
        }}
        detent="content-height"
        dragCloseThreshold={0.3}
        disableScrollLocking>
        <Sheet.Container>
          <Sheet.Header>
            {quickbet ? (
              <div className="!py-6 !px-4 flex justify-between items-center bg-surface-2 rounded-t-md text-Neutral-6">
                {quickbetHeader}
              </div>
            ) : (
              <div className="py-3 flex justify-center bg-surface-2 rounded-t-md ">
                <span className="h-2 w-14 rounded-2xl bg-Neutral-3"></span>
              </div>
            )}
          </Sheet.Header>
          <Sheet.Content className="bg-surface-2">{fullComp}</Sheet.Content>
        </Sheet.Container>
        {/* Always rendered: react-modal-sheet resolves its parts by child type, and a
            conditional here leaves a `false` child that stops the sheet mounting at
            all. The docked variant hides it in CSS instead. */}
        <Sheet.Backdrop
          onClick={() => {
            onToggle(false)
            // Clear outcome selection when closing to allow re-selection
            if (onCancel) onCancel()
          }}
          style={{ cursor: 'pointer' }}
        />
      </Sheet>
    ) : (
      fullComp
    )
  }
  const handleSwitchToPlayMoneyFromCountryBlock = () => {
    dispatch(setActiveCurrency('OOM'))
    storage('lastRealMoneyCurrency').set('OOM')
    storage('userCurrency').set('OOM')
  }

  const handleSwitchToPlayMoneyFromRealMoneyBlock = () => {
    dispatch(setActiveCurrency('OOM'))
    storage('userCurrency').set('OOM')
    toastService('pushNotification', {
      title: i18n('currency_selector.currency_changed'),
      icon: <OomsIcon width="48px" />,
    })
    onToggle(false)
  }

  const renderBlockedPanel = (reason, { compact = false } = {}) => {
    const needsPlayMoneySwitch =
      reason === PLACE_BET_BLOCK_REASON.COUNTRY_BLOCKED ||
      reason === PLACE_BET_BLOCK_REASON.REAL_MONEY_NOT_AVAILABLE

    return renderPlaceBetTemplate(
      <PlaceBetBlockedContent
        reason={reason}
        question={question}
        outcome={outcome}
        message={pauseMessage}
        compact={compact}
        onSwitchToPlayMoney={
          needsPlayMoneySwitch
            ? reason === PLACE_BET_BLOCK_REASON.COUNTRY_BLOCKED
              ? handleSwitchToPlayMoneyFromCountryBlock
              : handleSwitchToPlayMoneyFromRealMoneyBlock
            : undefined
        }
      />
    )
  }

  const renderOrderConflictPanel = () => {
    const isSellContext = activeTab === 'sell'

    const handlePlaceBet = () => {
      if (orderConflictData?.onRetry) {
        orderConflictData.onRetry(true, dontAskAgain) // Pass cancel_conflicting_orders and dontAskAgain
      }
      setShowOrderConflict(false)
      setOrderConflictData(null)
      setDontAskAgain(false) // Reset checkbox state
    }

    const handleDeposit = () => {
      mixpanelService.trackClick({
        component: CLICK_COMPONENT.DEPOSIT_ENTRY,
        element: CLICK_ELEMENT.CTA,
        source: CLICK_SOURCE.BET_SLIP_CONFLICT,
        destination: depositUrl(currencyId, 'bet_slip_conflict'),
        market_id: question?.id,
        market_title: question?.title,
      })
      navigate(depositUrl(currencyId, 'bet_slip_conflict'))
      setShowOrderConflict(false)
      setOrderConflictData(null)
      setDontAskAgain(false) // Reset checkbox state
    }

    const handleBack = () => {
      setShowOrderConflict(false)
      setOrderConflictData(null)
      setDontAskAgain(false) // Reset checkbox state
    }

    return renderPlaceBetTemplate(
      <div className="flex flex-col py-6 px-4" data-testid="place-bet-order-conflict-panel">
        <div className="flex gap-4 items-center">
          <ArrowRightTailed
            width="14px"
            height="14px"
            fillcolor="currentColor"
            className="rotate-180 cursor-pointer"
            onClick={() => {
              setShowOrderConflict(false)
            }}
          />
          <h3 className="font-bold text-xl mb-1 text-Neutral-6">{i18n('place_bet.order_update')}</h3>
        </div>

        <div className="space-y-3 my-6 text-left w-full">
          {isSellContext ? (
            <p className="text-sm text-Neutral-4">{i18n('place_bet.sell_order_conflict_description')}</p>
          ) : (
            <>
              <p className="text-sm text-Neutral-4">{i18n('place_bet.order_conflict_description')}</p>
              {currencyId !== 'OOM' && (
                <p className="text-sm text-Neutral-4">{i18n('place_bet.order_conflict_options')}</p>
              )}
            </>
          )}

          {/* Don't ask again checkbox */}
          <div className="flex items-center gap-2">
            <CheckControl id="dont-ask-again" active={dontAskAgain} onToggle={() => setDontAskAgain(!dontAskAgain)} />
            <label htmlFor="dont-ask-again" className="text-sm text-Neutral-6">
              {i18n('place_bet.dont_ask_again_always_cancel')}
            </label>
          </div>
        </div>

        {/* Buttons */}
        <div className="flex gap-2 w-full">
          {isSellContext ? (
            <>
              <Button variant="primary" type="outlined" size="large" className="flex-1" onClick={handleBack}>
                {i18n('place_bet.back')}
              </Button>
              <Button variant="primary" type="solid" size="large" className="flex-1" onClick={handlePlaceBet}>
                {i18n('place_bet.sell')}
              </Button>
            </>
          ) : (
            <>
              {currencyId !== 'OOM' && (
                <Button variant="primary" type="outlined" size="large" className="flex-1" onClick={handleDeposit}>
                  {i18n('place_bet.deposit')}
                </Button>
              )}
              <Button variant="primary" type="solid" size="large" className="flex-1" onClick={handlePlaceBet}>
                {i18n('place_bet.place_bet')}
              </Button>
            </>
          )}
        </div>
      </div>
    )
  }
  const renderBetPlaced = count => {
    const toggle = () => {
      if (betPlaced) {
        setBetPlaced(false)
        if (buyPlaceBetRef.current) buyPlaceBetRef.current.calculateInUSDValue()
      }
      onToggle()
    }
    return (
      <div
        className={classNames('place-bet', {
          '!border-b-0': quickbet || isMobileSize,
          // Same level as the market-page ground it sits on, so it needs the outline
          'border border-solid border-Neutral-3': !quickbet && !isMobileSize,
        })}>
        <div className="place-bet__header" onClick={() => toggle()}>
          {!quickbet && isMobileSize && (
            <Crevron width="12px" height="8px" fillcolor="currentColor" direction={open ? 'down' : 'up'} />
          )}
        </div>
        <div className="place-bet__content p-4">
          <div className="place-bet__blocked">
            <div className="place-bet__blocked--icon" style={{ background: '#00D966' }}>
              <CoinsIcon width="21px" fillcolor="white" />
            </div>
            <span
              className="place-bet__blocked--text"
              dangerouslySetInnerHTML={{
                __html: i18n('place_bet.you_placed_a_bet', {
                  count: count,
                }),
              }}></span>
            <Button
              variant="primary"
              type="solid"
              size="full"
              className="place-bet__main-button mt-3"
              onClick={() => {
                if (buyPlaceBetRef.current) buyPlaceBetRef.current.calculateInUSDValue()
                setBetPlaced(false)
              }}>
              {i18n('place_bet.bet_again')}
            </Button>
          </div>
        </div>
      </div>
    )
  }
  const BetTypeSelector = ({
    activeType,
    onTypeChange,
    orderType,
    onOrderTypeChange,
    sellLimitDisabled = false,
  }) => {
    const limitToggleDisabled = activeType === 'sell' && sellLimitDisabled

    return (
      <div
        className={classNames('flex justify-between items-center w-full gap-1 !px-5 ', {
          '!pt-5': !quickbet,
        })}>
        <div className="flex gap-1" role="tablist" aria-label={i18n('place_bet.buy') + '/' + i18n('place_bet.sell')}>
          <button
            type="button"
            role="tab"
            data-testid="place-bet-tab-buy"
            aria-selected={activeType === 'buy'}
            onClick={() => onTypeChange('buy')}
            className={classNames(
              'h-8 !px-4 rounded-full text-xs font-bold transition-colors outline-offset-2 focus-visible:outline-[var(--color-brand-04)]',
              activeType === 'buy'
                ? 'bg-[var(--color-brand-02)] text-[var(--color-contrast-02)]'
                : 'text-Neutral-4 hover:bg-surface-3'
            )}>
            {i18n('place_bet.buy')}
          </button>
          <button
            type="button"
            role="tab"
            data-testid="place-bet-tab-sell"
            aria-selected={activeType === 'sell'}
            onClick={() => onTypeChange('sell')}
            className={classNames(
              'h-8 !px-4 rounded-full text-xs font-bold transition-colors outline-offset-2 focus-visible:outline-[var(--color-brand-04)]',
              activeType === 'sell'
                ? 'bg-[var(--color-brand-02)] text-[var(--color-contrast-02)]'
                : 'text-Neutral-4 hover:bg-surface-3'
            )}>
            {i18n('place_bet.sell')}
          </button>
        </div>
        {/* ? TODO remove the staff and rethink the canonical solution */}
        {question.canonical_currency !== 'BTC' && question.order_book_enabled && (
          <OrderTypeToggle orderType={orderType} onChange={onOrderTypeChange} disabled={limitToggleDisabled} />
        )}
      </div>
    )
  }
  /**
   * ? Main content
   */
  const renderPlaceBetMainContent = () => {
    return renderPlaceBetTemplate(
      <div
        className={classNames('flex flex-col w-full', {
          'rounded-[var(--radius-sm)]': !quickbet,
        })}>
        <BetTypeSelector
          activeType={activeTab}
          orderType={orderType}
          onTypeChange={handleTabChange}
          onOrderTypeChange={type => {
            setOrderType(type)
          }}
          sellLimitDisabled={sellLimitDisabled}
        />

        {activeTab === 'buy' ? (
          <BuyPlaceBet
            ref={buyPlaceBetRef}
            question={question}
            outcome={outcome}
            onToggle={onToggle}
            quickbet={quickbet}
            onChangeOutcome={onChangeOutcome}
            setBetPlaced={setBetPlaced}
            onWagerAction={onWagerAction}
            position={position}
            place={place}
            marketType={orderType}
            setPosition={setPosition}
            onChangeToLimitOrder={handleChangeToLimitOrder}
            onOrderConflict={data => {
              setOrderConflictData(data)
              setShowOrderConflict(true)
            }}
          />
        ) : (
          <SellPlaceBet
            ref={sellPlaceBetRef}
            question={question}
            outcome={outcome}
            wager={wagerSelected}
            onToggle={onToggle}
            quickbet={quickbet}
            onChangeOutcome={onChangeOutcome}
            setBetPlaced={setBetPlaced}
            onWagerAction={onWagerAction}
            place={place}
            marketType={orderType}
            setActiveTab={handleTabChange}
            onChangeToLimitOrder={handleChangeToLimitOrder}
            inMarketPage={inMarketPage}
            onOrderConflict={data => {
              setOrderConflictData(data)
              setShowOrderConflict(true)
            }}
          />
        )}
      </div>,
      null,
      containerClassesList
    )
  }

  const containerClassesList = classNames('place-bet__content', {
    'place-bet__content--quickbet': quickbet,
  })

  const count = question && question.wagers && question.wagers.filter(w => w.status === WAGER_STATUS.PURCHASE).length

  const handleChangeToLimitOrder = (price = null) => {
    setOrderType('limit')

    // If we're in a market page and have a price, trigger orderbook actions
    if (inMarketPage && price !== null) {
      // Set the selected limit price in redux
      dispatch(setSelectedLimitPrice(price))

      // Open the orderbook
      onOpenOrderbook()

      // Scroll to orderbook
      setTimeout(() => {
        onScrollToOrderbook()
      }, 100)

      // Select the order with that price
      setTimeout(() => {
        onSelectOrderbookPrice(price)
      }, 200)
    }
  }

  const blockReason = getPlaceBetBlockReason({
    question,
    outcome,
    currencyId,
    fromAllowedCountry,
    tradingPaused,
  })

  if (blockReason) return renderBlockedPanel(blockReason)
  else if (showOrderConflict) return renderOrderConflictPanel()
  else if (!quickbet && betPlaced && lastBetStatus === constants.COMPLETE && count) return renderBetPlaced(count)
  else return renderPlaceBetMainContent()
}

export default PlaceBet
